import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'tea-component';
import { useIntl, useParams, useLocation, history } from 'umi';
import RangePicker from '@/pages/home/components/RangePicker';
import moment, { Moment, MomentInput } from 'moment';
import { formatDayTime, formatWeekTime, formatMonthTime } from '@home/pages/studio/utils';
import { LANGUAGE_EN } from '@home/_config/config';
import { ShortcutOptionDay, ShortcutOptionWeek, ShortcutOptionMonth } from './_config';
import SingleSelect from '@home/components/SingleSelect';
import { getName } from '@home/utils/common';
import { getSelectorList } from '@home/services/studio';
import Loading from '@/pages/dashboard/components/Loadings';
import './index.scss';
import { TParamsType } from '@home/pages/studio/components/CommonTab/_type';
import { Placement } from "popper.js";

export interface ICustomFilterConfig {
  key: string;
  label: { en: string; cn: string; };
  defaultValue: string;
  options: Array<{
    value: string;
    label: { en: string; cn: string; };
  }>;
  tooltip?: { en: string; cn: string; };
  searchable?: boolean;
  width?: number;
}

export type Granularity = 'date' | 'week' | 'month';

type TDateUnit = 'day' | 'week' | 'month';

interface IDateRuleConfig {
  value?: string;
  offset?: number;
  unit?: TDateUnit;
  useBusinessTimeZone?: boolean;
}

interface IResolvedDateRule {
  value: Moment;
  useBusinessTimeZone: boolean;
}

type TDateLimitInput = IDateRuleConfig;

export interface IGranularityDateConfig {
  minDate?: TDateLimitInput;
  maxDate?: TDateLimitInput;
  defaultValue?: [TDateLimitInput, TDateLimitInput];
}

interface IProps {
  children?: React.ReactNode;
  granularity?: Granularity;
  granularityOptions?: Granularity[];
  customFilters?: ICustomFilterConfig[];
  defaultTimeValue?: [Moment, Moment];
  shortcutOption?: any;
  hasExtraFilter?: boolean;
  gameCodes?: string;
  paymentTypeFilterEnName?: string;
  paymentTypeFilterCnName?: string;
  paymentTypeFilterEnTooltip?: string;
  paymentTypeFilterCnTooltip?: string;
  defaultVal?: string;
  paymentTypeItemTooltip: {
    [key: string]: {
      en: string;
      cn: string;
    };
  };
  paymentTypeItemTooltipPlacement: Placement;
  select_key?: string[];
  showDatePicker?: boolean;
  tabId?: string;
  timeZone?: number;
  timeFormat?: string;
  showShortcut?: boolean;
  singleSelect?: boolean;
  dateConfig?: Partial<Record<Granularity, IGranularityDateConfig>>;
}

interface GranularityConfig {
  getDefaultTimeValue: (timeZone: number) => [Moment, Moment];
  formatTime: (value: [Moment, Moment]) => string[];
  type: string;
  isSingle: (start: MomentInput, end: MomentInput) => boolean;
  startOfWeek: number;
  defualtSelectedShortcut?: any;
  shortcutOption?: any;
}

const granularityConfigs: Record<Granularity, GranularityConfig> = {
  date: {
    getDefaultTimeValue: (timeZone: number) => [
      moment().utcOffset(timeZone).startOf('day').subtract(1, 'days'),
      moment().utcOffset(timeZone).endOf('day').subtract(1, 'days'),
    ],
    formatTime: formatDayTime,
    type: 'daily',
    isSingle: (start, end) => moment(end).diff(start, 'days') === 0,
    startOfWeek: 0,
    shortcutOption: ShortcutOptionDay
  },
  week: {
    getDefaultTimeValue: (timeZone: number) => [
      moment().utcOffset(timeZone).subtract(1, 'week').startOf('isoWeek'),
      moment().utcOffset(timeZone).subtract(1, 'week').endOf('isoWeek'),
    ],
    formatTime: formatWeekTime,
    type: 'weekly',
    isSingle: (start, end) => moment(end).diff(start, 'days') === 6,
    startOfWeek: 1,
    defualtSelectedShortcut: ShortcutOptionWeek?.week[1],
    shortcutOption: ShortcutOptionWeek
  },
  month: {
    getDefaultTimeValue: (timeZone: number) => [
      moment().utcOffset(timeZone).subtract(1, 'month').startOf('month'),
      moment().utcOffset(timeZone).subtract(1, 'month').endOf('month'),
    ],
    formatTime: formatMonthTime,
    type: 'monthly',
    isSingle: (start, end) => moment(start).isSame(end, 'month'),
    startOfWeek: 0,
    defualtSelectedShortcut: ShortcutOptionMonth?.month[0],
    shortcutOption: ShortcutOptionMonth
  },
};

export default function HomeFilter(props: IProps) {
  const {
    children,
    granularity: defaultGranularity = 'week',
    granularityOptions,
    customFilters = [],
    hasExtraFilter = false,
    select_key: selectKey = [],
    gameCodes,
    showDatePicker = true,
    paymentTypeFilterEnName = 'Payment Type', 
    paymentTypeFilterCnName = '付费类型',
    defaultVal = 'Paid only',
    paymentTypeFilterCnTooltip = 'Product Payment Type', 
    paymentTypeFilterEnTooltip = '产品付费类型', 
    paymentTypeItemTooltip = {
      'Free only': {
        en: 'Applies only to revenue/sales-related data',
        cn: '此筛选器仅复用至收入相关数据'
      },
      "Paid only": {
        en: 'Applies only to revenue/sales-related data',
        cn: '此筛选器仅复用至收入相关数据'
      },
    },
    paymentTypeItemTooltipPlacement= 'right',
    tabId,
    timeZone = 0,
    timeFormat,
    showShortcut = true,
    singleSelect = false,
    dateConfig,
    ...restProps
  } = props || {};

  const { locale } = useIntl();
  const location = useLocation();
  const isEn = locale === LANGUAGE_EN;

  const granularityUnitMap: Record<Granularity, TDateUnit> = useMemo(() => ({
    date: 'day',
    week: 'week',
    month: 'month',
  }), []);

  const getBaseMoment = useCallback((useBusinessTimeZone: boolean, tz: number): Moment => {
    return useBusinessTimeZone ? moment().utcOffset(tz) : moment();
  }, []);

  const normalizeComparableMoment = useCallback((date: Moment, useBusinessTimeZone: boolean, tz: number): Moment => {
    const nextDate = moment(date);
    return useBusinessTimeZone ? nextDate.utcOffset(tz, true) : nextDate;
  }, []);

  const resolveDateInput = useCallback((
    dateInput: TDateLimitInput,
    granularity: Granularity,
    bound: 'min' | 'max',
    tz: number,
  ): IResolvedDateRule | null => {
    const applyBoundary = (date: Moment): Moment => {
      if (granularity === 'date') {
        return bound === 'min' ? date.startOf('day') : date.endOf('day');
      }
      if (granularity === 'week') {
        return bound === 'min' ? date.startOf('isoWeek') : date.endOf('isoWeek');
      }
      return bound === 'min' ? date.startOf('month') : date.endOf('month');
    };

    const createResolvedRule = (date: Moment, useBusinessTimeZone: boolean): IResolvedDateRule => ({
      value: applyBoundary(date),
      useBusinessTimeZone,
    });

    const resolveRelativeInput = (
      offset: number,
      unit: TDateUnit,
      useBusinessTimeZone: boolean,
    ): IResolvedDateRule => {
      return createResolvedRule(getBaseMoment(useBusinessTimeZone, tz).add(offset, unit), useBusinessTimeZone);
    };

    const {
      value,
      offset,
      unit,
      useBusinessTimeZone = false,
    } = dateInput || {};

    if (typeof offset === 'number') {
      return resolveRelativeInput(offset, unit || granularityUnitMap[granularity], useBusinessTimeZone);
    }

    if (value) {
      const absoluteDate = moment(value);
      if (!absoluteDate.isValid()) return null;
      const normalizedDate = useBusinessTimeZone
        ? absoluteDate.utcOffset(tz, true)
        : absoluteDate;
      return createResolvedRule(normalizedDate, useBusinessTimeZone);
    }

    return null;
  }, [getBaseMoment, granularityUnitMap]);

  const getDefaultTime = useCallback((g: Granularity, tz: number): [Moment, Moment] => {
    const cfgDefault = dateConfig?.[g]?.defaultValue;
    if (cfgDefault) {
      const start = resolveDateInput(cfgDefault[0], g, 'min', tz);
      const end = resolveDateInput(cfgDefault[1], g, 'max', tz);
      if (start && end) {
        return [start.value, end.value];
      }
    }
    return granularityConfigs[g].getDefaultTimeValue(tz);
  }, [dateConfig, resolveDateInput]);

  const [currentGranularity, setCurrentGranularity] = useState<Granularity>(() => {
    const urlGranularity = new URLSearchParams(location.search).get('granularity');
    const validOptions: readonly string[] = Array.isArray(granularityOptions) && granularityOptions.length > 0
      ? granularityOptions
      : [defaultGranularity];
    if (urlGranularity && validOptions.includes(urlGranularity)) {
      return urlGranularity as Granularity;
    }
    return defaultGranularity;
  });
  const normalizedGranularityOptions = useMemo(() => {
    if (Array.isArray(granularityOptions) && granularityOptions.length > 0) {
      return granularityOptions;
    }
    return [currentGranularity];
  }, [granularityOptions, currentGranularity]);
  const showGranularitySelect = normalizedGranularityOptions.length > 1;
  const config = granularityConfigs[currentGranularity];

  const [timeValue, setTimeValue] = useState<[Moment, Moment]>(() => getDefaultTime(currentGranularity, timeZone));
  const [payType, setPayType] = useState<string>();
  const [payTypeOptions, setPayTypeOptions] = useState<{ code: string; name_en: string; name_cn: string; }[]>([]);
  const [filterLoading, setFilterLoading] = useState(true);
  const [selectorList, setSelectorList] = useState([{}]);
  const params = useParams() as TParamsType;
  const { studioId } = params || {};

  const [customFilterValues, setCustomFilterValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    customFilters.forEach(f => {
      initial[f.key] = f.defaultValue;
    });
    return initial;
  });

  const handleCustomFilterChange = useCallback((key: string, value: string): void => {
    setCustomFilterValues(prev => ({ ...prev, [key]: value }));
    setIsChange(true);
  }, []);

  useEffect(() => {
    if (tabId) {
      setTimeValue(getDefaultTime(currentGranularity, timeZone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, timeZone]);

  const [isChange, setIsChange] = useState(false);

  const filter = useMemo(() => {
    const timeVal = config.formatTime(timeValue);
    const startTime = timeFormat ? moment(timeVal[0]).format(timeFormat) : timeVal[0];
    const endTime = timeFormat ? moment(timeVal[1]).format(timeFormat) : timeVal[1];
    return {
      start_time: startTime,
      end_time: endTime,
      date_type: config.type,
      isSingle: config.isSingle(timeValue[0], timeValue[1]),
      payType,
      selectorList,
      isChange,
      ...customFilterValues,
    };
  }, [timeValue, config, payType, selectorList, isChange, customFilterValues, timeFormat]);

  const combinedShortcutOptions = useMemo(() => {
    const combined: Record<string, any> = {};
    normalizedGranularityOptions.forEach(g => {
      const gConfig = granularityConfigs[g];
      if (gConfig.shortcutOption) {
        Object.assign(combined, gConfig.shortcutOption);
      }
    });
    return combined;
  }, [normalizedGranularityOptions]);

  const granularityDefaultValues = useMemo(() => {
    const defaults: Record<string, [Moment, Moment]> = {};
    normalizedGranularityOptions.forEach(g => {
      defaults[g] = getDefaultTime(g, timeZone);
    });
    return defaults;
  }, [getDefaultTime, normalizedGranularityOptions, timeZone]);

  const customFilterSelectOptions = useMemo(() => {
    return customFilters.map(filterConfig => ({
      key: filterConfig.key,
      name: getName(isEn, filterConfig.label.en, filterConfig.label.cn),
      tooltip: filterConfig.tooltip
        ? getName(isEn, filterConfig.tooltip.en, filterConfig.tooltip.cn)
        : undefined,
      searchable: filterConfig.searchable ?? false,
      width: filterConfig.width,
      options: filterConfig.options.map(opt => ({
        value: opt.value,
        text: getName(isEn, opt.label.en, opt.label.cn),
      })),
    }));
  }, [customFilters, isEn]);

  const handleRangePickerChange = useCallback((value: any, newType?: string): void => {
    if (newType && newType !== currentGranularity) {
      const newGranularity = newType as Granularity;
      setCurrentGranularity(newGranularity);
      setTimeValue(getDefaultTime(newGranularity, timeZone));
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('granularity', newGranularity);
      history.replace({ pathname: history.location.pathname, search: searchParams.toString() });
    } else {
      setTimeValue(value);
    }
    setIsChange(true);
  }, [currentGranularity, timeZone, getDefaultTime]);

  const singleSelectAutoRange = singleSelect && currentGranularity !== 'week'
    ? { length: 0, timeType: currentGranularity === 'month' ? 'month' : 'day' }
    : undefined;

  const dateRangeChecks = useMemo(() => {
    const getDayIndex = (date: Moment): number => {
      return date.year() * 372 + date.month() * 31 + date.date();
    };

    const getMonthIndex = (date: Moment): number => date.year() * 12 + date.month();

    const getWeekIndex = (date: Moment): number => {
      const weekStart = moment(date).startOf('isoWeek');
      return getDayIndex(weekStart);
    };

    const resolveMinMax = (g: Granularity): { min: IResolvedDateRule | null; max: IResolvedDateRule | null; } => {
      const cfgMin = dateConfig?.[g]?.minDate;
      const cfgMax = dateConfig?.[g]?.maxDate;
      if (!cfgMin && !cfgMax) return { min: null, max: null };
      const min = cfgMin ? resolveDateInput(cfgMin, g, 'min', timeZone) : null;
      const max = cfgMax ? resolveDateInput(cfgMax, g, 'max', timeZone) : null;
      return { min, max };
    };

    const buildDayCheck = (): ((date: Moment) => boolean) | undefined => {
      const { min, max } = resolveMinMax('date');
      if (!min && !max) return undefined;
      return (date: Moment): boolean => {
        if (min) {
          const currentDayIndex = getDayIndex(normalizeComparableMoment(date, min.useBusinessTimeZone, timeZone));
          const minDayIndex = getDayIndex(min.value);
          if (currentDayIndex < minDayIndex) return false;
        }
        if (max) {
          const currentDayIndex = getDayIndex(normalizeComparableMoment(date, max.useBusinessTimeZone, timeZone));
          const maxDayIndex = getDayIndex(max.value);
          if (currentDayIndex > maxDayIndex) return false;
        }
        return true;
      };
    };

    const buildWeekCheck = (): ((date: Moment) => boolean) | undefined => {
      const { min, max } = resolveMinMax('week');
      if (!min && !max) return undefined;
      return (date: Moment): boolean => {
        if (min) {
          const currentWeekIndex = getWeekIndex(normalizeComparableMoment(date, min.useBusinessTimeZone, timeZone));
          const minWeekIndex = getWeekIndex(min.value);
          if (currentWeekIndex < minWeekIndex) return false;
        }
        if (max) {
          const currentWeekIndex = getWeekIndex(normalizeComparableMoment(date, max.useBusinessTimeZone, timeZone));
          const maxWeekIndex = getWeekIndex(max.value);
          if (currentWeekIndex > maxWeekIndex) return false;
        }
        return true;
      };
    };

    const buildMonthCheck = (): ((date: Moment) => boolean) | undefined => {
      const { min, max } = resolveMinMax('month');
      if (!min && !max) return undefined;
      const minMonthIndex = min ? getMonthIndex(min.value) : null;
      const maxMonthIndex = max ? getMonthIndex(max.value) : null;
      return (date: Moment): boolean => {
        if (minMonthIndex !== null) {
          const minRule = min as IResolvedDateRule;
          const currentMinMonthIndex = getMonthIndex(
            normalizeComparableMoment(date, minRule.useBusinessTimeZone, timeZone),
          );
          if (currentMinMonthIndex < minMonthIndex) return false;
        }
        if (maxMonthIndex !== null) {
          const maxRule = max as IResolvedDateRule;
          const currentMaxMonthIndex = getMonthIndex(
            normalizeComparableMoment(date, maxRule.useBusinessTimeZone, timeZone),
          );
          if (currentMaxMonthIndex > maxMonthIndex) return false;
        }
        return true;
      };
    };

    const dayCheck = buildDayCheck();
    const weekCheck = buildWeekCheck();
    const monthCheck = buildMonthCheck();
    if (!dayCheck && !weekCheck && !monthCheck) return undefined;
    return { dayCheck, weekCheck, monthCheck };
  }, [dateConfig, normalizeComparableMoment, resolveDateInput, timeZone]);

  const renderRangePicker = () => (
    <RangePicker
      type={currentGranularity}
      value={timeValue}
      className="search-border"
      defualtSelectedShortcut={config?.defualtSelectedShortcut || {}}
      typeList={normalizedGranularityOptions}
      language={isEn ? 'en' : 'cn'}
      showSelectType={showGranularitySelect}
      selectClassName={showGranularitySelect ? 'dbh-studio-home-granularity-select' : ''}
      showShortcutTypeTitle={!showGranularitySelect}
      showShortcut={showShortcut}
      startOfWeek={config.startOfWeek}
      singleWeek={singleSelect && currentGranularity === 'week'}
      onChange={handleRangePickerChange}
      {...(showGranularitySelect ? { defaultValue: granularityDefaultValues } : {})}
      {...(singleSelectAutoRange ? { autoRange: singleSelectAutoRange } : {})}
      {...(dateRangeChecks ? {
        disabledDate: {
          ...(dateRangeChecks.dayCheck ? { date: dateRangeChecks.dayCheck } : {}),
          ...(dateRangeChecks.weekCheck ? { week: dateRangeChecks.weekCheck } : {}),
          ...(dateRangeChecks.monthCheck ? { month: dateRangeChecks.monthCheck } : {}),
        },
        ...(dateRangeChecks.monthCheck ? {
          disabledMonth: dateRangeChecks.monthCheck,
          customOption: { month: { disabledMonth: dateRangeChecks.monthCheck } },
        } : {}),
      } : {})}
      overlayClassName="dbh-studio-home-range-picker-overlay"
      {...(showShortcut ? { setShortcutOption: () => combinedShortcutOptions } : {})}
    />
  );
  // 获取付费选项
  useEffect(() => {
    if (hasExtraFilter) {
      setFilterLoading(true);
      const params = {
        studio_code: studioId,
        game_codes: gameCodes,
        select_key: selectKey
      };
      getSelectorList(params).then((res) => {
        // 把所有游戏的筛选器合并
        const mergedSelectList = (() => {
          // 创建一个 Map，用于按 select_key 分组并合并 select_list 项
          const selectMap = new Map();

          // 遍历所有游戏数据
          for (const game of res) {
            for (const select of game?.select_list || []) {
              // 获取 select_key，统一为小写作为 groupKey
              const groupKey = String(select?.select_key).toLowerCase();

              // 如果该 groupKey 还没有初始化，先创建一个新的 Map 存放 code 映射
              if (!selectMap.has(groupKey)) {
                selectMap.set(groupKey, new Map());
              }

              // 获取当前 groupKey 对应的 code -> item Map
              const codeMap = selectMap.get(groupKey);

              // 遍历当前 select_list 中的所有项
              for (const item of select?.select_list || []) {
                const itemKey = item?.code?.toString();
                if (!itemKey) continue; // 跳过无效的 code

                const existingItem = codeMap.get(itemKey);

                if (!existingItem) {
                  // 如果这个 code 还不存在，直接添加副本
                  codeMap.set(itemKey, { ...item });
                } else {
                  // 如果已存在该 code 的条目，尝试合并 sub_select（如果存在）
                  if (item.sub_select?.length) {
                    // 如果旧数据没有 sub_select，初始化为空数组
                    if (!existingItem.sub_select) {
                      existingItem.sub_select = [];
                    }

                    // 构建已有 sub_select 的 code 集合用于去重
                    const existingSubCodes = new Set(existingItem.sub_select.map((s: { code: string; }) => s.code));

                    // 把当前 item 中未重复的 sub_select 项加入
                    for (const sub of item.sub_select) {
                      if (!existingSubCodes.has(sub.code)) {
                        existingItem.sub_select.push(sub);
                      }
                    }
                  }
                }
              }
            }
          }

          // 最终将 Map 转换为数组格式，返回结构符合原格式
          return Array.from(selectMap, ([select_key, codeMap]) => ({
            select_key,
            select_list: Array.from(codeMap.values()),
          }));
        })();

        setSelectorList(mergedSelectList);

        const payTypeOpts = mergedSelectList?.find((item: any) => item.select_key === 'payment_type');
        const selectList = payTypeOpts?.select_list || [];
        // ALL排序在第一个
        let allOpt;
        const otherOpts: any[] = [];
        selectList?.forEach((item: any) => {
          if (item.code === 'All') {
            allOpt = item;
          } else {
            otherOpts.push(item);
          }
        });
        if (allOpt) otherOpts.unshift(allOpt);
        setPayTypeOptions(otherOpts);
        setPayType(defaultVal || otherOpts[0]?.code || 'All');
      }).catch((err) => console.log("🚀 ~ getSelectorList ~ err:", err)).finally(() => setFilterLoading(false));
    } else {
      setFilterLoading(false);
    }
  }, [gameCodes, hasExtraFilter, defaultVal, selectKey, studioId]);

  const newPayTypeOptions = useMemo(() => payTypeOptions?.map(item => {
    const { code, name_en, name_cn } = item;
    // 特殊处理all需要展示成全部
    const name = getName(isEn, name_en, name_cn === 'All' ? '全部' : name_cn);
    const tooltipText = paymentTypeItemTooltip?.[name_en]?.[isEn ? 'en' : 'cn'] || '';
    return {
      value: code,
      text: name,
      tooltip: tooltipText,
      tooltipPlacement: paymentTypeItemTooltipPlacement
    };
  }), [isEn, payTypeOptions, paymentTypeItemTooltip, paymentTypeItemTooltipPlacement]);

  return (
    <ConfigProvider locale={isEn ? 'zh' : 'en'}>
      <div className="dbo-ingame-analytics" style={{ height: '100%' }}>
        {showDatePicker && <div className="dbh-studio-operate dbh-studio-home-filter">
          <div className="search-item search-item-date">
            {renderRangePicker()}
          </div>
          {
            selectKey?.includes('payment_type') && <div><SingleSelect
              className='dbh-studio-home-paymemt-type-select'
              matchButtonWidth
              options={newPayTypeOptions}
              value={payType}
              searchable={false}
              locale={locale}
              onChange={setPayType}
              name={getName(isEn, paymentTypeFilterEnName, paymentTypeFilterCnName)}
              tooltip={getName(isEn, paymentTypeFilterCnTooltip, paymentTypeFilterEnTooltip)}
            /></div>
          }
          {customFilterSelectOptions.map(filterOpt => (
            <div key={filterOpt.key}>
              <SingleSelect
                className="dbh-studio-home-custom-filter-select"
                matchButtonWidth
                options={filterOpt.options}
                value={customFilterValues[filterOpt.key]}
                searchable={filterOpt.searchable}
                locale={locale}
                onChange={(val: string) => handleCustomFilterChange(filterOpt.key, val)}
                name={filterOpt.name}
                tooltip={filterOpt.tooltip}
                {...(filterOpt.width ? { style: { width: `${filterOpt.width}px` } } : {})}
              />
            </div>
          ))}
        </div>
        }
        {filterLoading ? <Loading className='dbh-studio-home-filter-loading' /> : children && React.Children.map(children, child => React.cloneElement(child, { filter, filterVal: filter, ...restProps }))}
      </div>
    </ConfigProvider>
  );
}
